/**
 * The **Sub-resource path** grammar, `res://file.tres::SubId`: Godot's text saver
 * writes it for every `[sub_resource]`, the binary loader gives an internal resource
 * the same path (`resource_format_binary.cpp:722`), and `ResourceLoader.load()` accepts it.
 * One string addresses an `ExtResource` resource file, a `SubResource` of the
 * scene, and a `SubResource` of another resource file.
 */
import { descendsFromClass } from '../godot/classdb/classBaseTypes.js';
import { warn } from '../logger.js';
import { findSubResource, parseResourceReference } from './SubResourceResolver.js';
/**
 * What Godot's text saver writes between an owning file and a sub-resource. A
 * `res://` path cannot contain a colon (Godot rejects it, and Windows forbids
 * it), so the separator is unambiguous.
 */
const SEPARATOR = '::';
/**
 * Split a resource path into the file to fetch and the sub-resource to read out
 * of it. A plain path yields just `filePath`, so callers can apply this
 * unconditionally.
 */
export function parseSubResourcePath(path) {
    const at = path.indexOf(SEPARATOR);
    if (at < 0)
        return { filePath: path };
    const subResourceId = path.slice(at + SEPARATOR.length);
    // A trailing separator with nothing after it names no sub-resource; treat it
    // as the file itself rather than inventing an empty id no lookup can match.
    if (subResourceId === '')
        return { filePath: path.slice(0, at) };
    return { filePath: path.slice(0, at), subResourceId };
}
/** Address the `[sub_resource id="subResourceId"]` declared inside `filePath`. */
function subResourcePath(filePath, subResourceId) {
    return `${filePath}${SEPARATOR}${subResourceId}`;
}
/** The fetchable file behind either form of path: what the byte layer is asked for. */
export function resourceFilePath(path) {
    return parseSubResourcePath(path).filePath;
}
/** Each `[ext_resource]` id to its path. A repeated id takes its last path, as `findExtResource` does. */
export function extResourcePathsById(resources) {
    return new Map(resources.map((r) => [r.id, r.path]));
}
/**
 * A reference inside a `.tres` as one path string: an `ExtResource` file, or a
 * `SubResource` as a **Sub-resource path** into `selfPath`. Null when the
 * reference is absent, malformed, or an undeclared `ExtResource` id. Every
 * producer of an address calls this, so the policy is written once.
 */
export function resolveRefToResourcePath(ref, extPathById, selfPath, gate) {
    if (!ref)
        return null;
    const parsed = parseResourceReference(ref);
    if (!parsed)
        return null;
    if (parsed.type === 'ExtResource')
        return extPathById.get(parsed.id) ?? null;
    if (gate) {
        const sub = findSubResource(gate.declared, parsed.id);
        // An undeclared id is a broken file, not a declined type. Say so, or the
        // `.tres` renders a default and nothing names the dangling id.
        if (!sub && gate.declared.length > 0) {
            warn(`[SubResource] "${parsed.id}" is referenced but not declared — ignoring it`);
        }
        if (!gate.accepts(sub))
            return null;
    }
    return subResourcePath(selfPath, parsed.id);
}
/** Accept any sub-resource of class `ancestor` or one it derives, for a reader that judges the subclass itself. */
export function subResourceClassGate(declared, ancestor) {
    return { declared, accepts: (sub) => sub !== undefined && descendsFromClass(sub.type, ancestor) };
}
/** Accept only sub-resources whose `type` is one of `types`. */
export function subResourceTypeGate(declared, types) {
    return { declared, accepts: (sub) => sub !== undefined && types.has(sub.type) };
}
//# sourceMappingURL=subResourcePath.js.map